// v1.9 P0: Alert 原子组件
// 4 variants + 自动 icon + title + dismissable
// 与 Badge 共用色板
import { type ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../Icon';

export type AlertVariant = 'success' | 'warning' | 'error' | 'info';

export interface AlertProps {
  variant?: AlertVariant;
  /** 标题 (加粗) */
  title?: ReactNode;
  /** 详细描述 */
  children?: ReactNode;
  /** 是否可关闭 (右上角 ✕) */
  dismissible?: boolean;
  /** 关闭回调 (controlled) */
  onDismiss?: () => void;
  /** 自定义 icon (默认按 variant 自动选) */
  icon?: string;
  /** 自定义 className */
  className?: string;
  /** role (默认 'alert') */
  role?: 'alert' | 'status';
}

const VARIANT_CLASS: Record<AlertVariant, string> = {
  success: 'bg-good/10 text-good border-good/30',
  warning: 'bg-warn/10 text-warn border-warn/30',
  error: 'bg-bad/10 text-bad border-bad/30',
  info: 'bg-info/10 text-info border-info/30',
};

const DEFAULT_ICON: Record<AlertVariant, string> = {
  success: 'check-circle',
  warning: 'alert-triangle',
  error: 'alert-circle',
  info: 'info',
};

export function Alert({
  variant = 'info',
  title,
  children,
  dismissible = false,
  onDismiss,
  icon,
  className,
  role = 'alert',
}: AlertProps) {
  const [internalDismissed, setInternalDismissed] = useState(false);
  if (internalDismissed) return null;

  function handleDismiss() {
    if (onDismiss) onDismiss();
    else setInternalDismissed(true);
  }

  return (
    // role="status" → <output> (原生 role=status), role="alert" → <div role="alert">
    // 用条件渲染, biome useSemanticElements 不会触发
    role === 'status' ? (
      <output
        className={twMerge(
          'flex items-start gap-2.5 px-3 py-2.5 border rounded-md text-sm',
          VARIANT_CLASS[variant],
          className,
        )}
      >
        <span className="shrink-0 mt-0.5" aria-hidden>
          <Icon name={icon ?? DEFAULT_ICON[variant]} size="sm" />
        </span>
        <div className="flex-1 min-w-0">
          {title && <div className="font-semibold mb-0.5">{title}</div>}
          {children && <div className="text-current/90 leading-relaxed">{children}</div>}
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 -mt-0.5 -mr-1 p-1 rounded hover:bg-black/5 transition-colors"
            aria-label="关闭"
          >
            <Icon name="x" size="sm" aria-hidden />
          </button>
        )}
      </output>
    ) : (
      <div
        role="alert"
        className={twMerge(
          'flex items-start gap-2.5 px-3 py-2.5 border rounded-md text-sm',
          VARIANT_CLASS[variant],
          className,
        )}
      >
        <span className="shrink-0 mt-0.5" aria-hidden>
          <Icon name={icon ?? DEFAULT_ICON[variant]} size="sm" />
        </span>
        <div className="flex-1 min-w-0">
          {title && <div className="font-semibold mb-0.5">{title}</div>}
          {children && <div className="text-current/90 leading-relaxed">{children}</div>}
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 -mt-0.5 -mr-1 p-1 rounded hover:bg-black/5 transition-colors"
            aria-label="关闭"
          >
            <Icon name="x" size="sm" aria-hidden />
          </button>
        )}
      </div>
    )
  );
}
